/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {ActionButton, Badge, defaultTheme, Flex, Icon, Provider, Text} from '@adobe/react-spectrum';
import classNames from 'classnames';
import {getFullUrl, hashToPath} from '@exc/url';
import {getOmegaEvent} from '@exc/events';
import {injectIntl} from 'react-intl';
import {IntlProvider} from '@quarry/intl';
import * as localizationStringsShell from './__localization__/ShellHeader';
import * as localizationStringsShellAction from './__localization__/ShellHeaderActions';
import metrics from '@adobe/exc-app/metrics';
import {NOOP} from '@exc/shared';
import PropTypes from 'prop-types';
import React from 'react';
import {RELEASE_TYPE} from '@exc/core/src/models/Solution';
import ShellHeaderActions from './ShellHeaderActions';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import SideNavMenuButton from './SideNavMenuButton';
import SpectrumV2Provider from '@react/react-spectrum/Provider';
import {updatePath} from '@exc/url/pathUpdate';
import Visibility from '@spectrum-icons/workflow/Visibility';

import './ShellHeader.scss';

const CoachMark = React.lazy(() => import('@react/react-spectrum/CoachMark'));
const I18nShellSkipMenu = React.lazy(() => import('./ShellSkipMenu'));
const ShellWorkspace = React.lazy(() => import('./ShellWorkspace'));

// This is an inexact constant. It'll require updating if header or styling
// changes.
const HEADER_TITLE_PROPORTION_LIMIT = .2;
const RELEASE_TYPE_VARIANTS = {
  [RELEASE_TYPE.ALPHA]: 'indigo',
  [RELEASE_TYPE.BETA]: 'seafoam',
  [RELEASE_TYPE.DEV]: 'yellow',
  [RELEASE_TYPE.POC]: 'fuchsia'
};

let searchMetrics;
let textCanvas;

/**
 * Returns an appropriate variation of the a short title to render based on the
 * the proportion it takes up of the screen size to avoid pushing menus offscreen.
 */
function shortTitleToRender(text, isWorkspacesAvailable, size) {
  if (size === 'S' && !isWorkspacesAvailable) {
    return text.replace('Adobe ', '');
  }

  textCanvas ??= document.createElement('canvas');
  const context = textCanvas.getContext('2d');

  // Creating a canvas is funny in tests, so just always returned the passed text there
  if (!context) {
    return text;
  }

  const headerText = document.querySelector('.spectrum-Shell-leftContainer h1');

  // Collapsed workspaces is roughly the same size as thee maximum size of title
  // so we half the limit the title can take up if it's present
  const limit = isWorkspacesAvailable ? HEADER_TITLE_PROPORTION_LIMIT / 2 : HEADER_TITLE_PROPORTION_LIMIT;

  if (headerText) {
    const headerCss = getComputedStyle(headerText);
    context.font = `${headerCss.getPropertyValue('font-size')} ${headerCss.getPropertyValue('font-family')}`;
  } else {
    context.font = '14px sans-serif';
  }

  if ((context.measureText(text).width / window.innerWidth) < limit) {
    return text;
  }

  const removedAdobe = text.replace('Adobe ', '');
  if (removedAdobe.length < text.length && ((context.measureText(removedAdobe).width / window.innerWidth) < limit)) {
    return removedAdobe;
  }

  return '';
}

/**
 * Update search path: just a wrapper around updatePath
 * with some metrics inside.
 */
export function updatePathSearch(searchArgument) {
  const {
    eventData = {},
    eventName = 'updatePathSearch called',
    hasSandbox = true,
    hasTenant = true,
    url
  } = searchArgument;

  // Ensure that the search metrics instance is created.
  searchMetrics || (searchMetrics = metrics.create('exc.UnifiedSearch'));
  searchMetrics.event(eventName, eventData);

  const param = hasSandbox && 'sname';
  searchMetrics.event(eventName, eventData);
  updatePath(url, false, hasTenant, param);
}

function HeroImage({hasTitle, icon, theme, title}) {
  let className;
  if (theme === 'lightest' && ['AdobeExperienceSubCloud', 'WorkfrontHero'].includes(icon.split('#')[1])) {
    className = 'light-border';
  } else if (theme === 'darkest' && icon.includes('AdobeExperiencePlatform')) {
    className = 'dark-border';
  }

  return (
    <Icon UNSAFE_className={classNames('hero-svg', {'no-title': !hasTitle})} size="M">
      <img alt={title} className={className} src={icon} />
    </Icon>
  );
}

let currentVisibleOverlay = null;

/**
 * Experience Cloud Shell Header component. Container for all shell sub-components.
 * @param {Object} <ObjectPattern> Props from parent component.
 * @returns {ReactElement} Markup for ShellHeader component.
 */
class ShellHeader extends React.Component {
  constructor(props) {
    super(props);
    this.metrics = metrics.create('exc.shell.Shell');
    this.state = {
      globalSearch: {open: false},
      skipMenuOpen: false,
      toastsSupressedCount: 0
    };
  }

  /**
   * Temporary function to ensure only one popover is open at a time.
   * It should be removed once the entire header is on V3
   */
  closeOverlaysOnMenuShow = (ref, nested) => {
    if (currentVisibleOverlay && !nested) {
      currentVisibleOverlay?.hide();
    }
    currentVisibleOverlay = ref?.current;
  };

  onToggleSidebar = e => this.props.onToggleSidebar(e?.detail?.query, true);

  /**
   * Handler for opening and closing Help Center menu.
   * Added for specific case of opening Help menu from solution.
   * @param {Event} e - Event object.
   * @param {boolean} shouldClose - Flag for differentiating explicitly closing with onHide callback
   */
  onToggleHelpCenter = (e, shouldClose) => {
    let helpCenterProps = this.props.helpCenterConfig;
    const {config, selectedTab} = e && e.detail || {};
    if (selectedTab && (selectedTab === 'support' || config)) {
      helpCenterProps = {...helpCenterProps, config, open: true, selectedTab};
    } else {
      helpCenterProps = {
        ...helpCenterProps,
        open: shouldClose ? false : !this.state.helpCenterProps?.open
      };
    }
    this.setState({helpCenterProps});
  };

  onToggleGlobalSearch = shouldClose => {
    const {globalSearch} = this.state;
    globalSearch.open = shouldClose ? false : !globalSearch.open;
    this.setState({globalSearch});
    // We need the global dialog value so we can hide the solution from the
    // accessibility tree in this case. This is on a timeout so that navigation
    // actions have time to complete ahead of this firing, otherwise they won't
    // work.
    setTimeout(() => this.props.onGlobalDialogOpen(globalSearch.open), 10);
  };

  onSkipMenuVisibilityChange = (skipMenuOpen, e) => {
    this.setState({skipMenuOpen});
    // Only track skip menu opening (not closing) in omega
    if (skipMenuOpen) {
      this.trackSkipMenuActions(e, 'skipMenu display');
    }
  };

  trackSkipMenuActions = ({target, type}, action) => {
    // We track focus for menu opening, as well as menu selections through
    // click (mouse click and enter key) and space bar keyDown
    if (type === 'focus' || type === 'press') {
      this.metrics.analytics.trackEvent(getOmegaEvent({
        action,
        element: action || target && target.innerText,
        type: 'button',
        widget: {name: 'skip menu', type: 'button'}
      }));
    }
  };

  /**
   * Sets the state with the a11y skip menu list
   * @param detail List of skip menu options recieved from module-runtime event.
   */
  setSkipMenuList = ({detail}) => this.setState({skipMenuList: detail});

  setToastSupressed = () => this.setState({toastsSupressedCount: this.state.toastsSupressedCount + 1});

  escapeKeyHandler = e => {
    if (e.key === 'Escape' && this.state.globalSearch.open) {
      const searchButton = document.querySelector('button.search-menu');
      const searchActiveInput = document.querySelector('input#global-search-input');
      const searchPlaceholderInput = document.querySelector('input#global-search-placeholder');
      const resultsContainer = document.querySelector('.dropdown-results-container');

      /**
       * Priority/order of focus:
       * 1. Search icon button
       * 2. Active search input
       * 3. Placeholder search input
       */
      if (searchButton) {
        // If search icon button is present, return focus to it.
        searchButton.focus();
      } else if (resultsContainer) {
        // If results are showing, return focus to the active search input generating the results.
        searchActiveInput.focus();
      } else if (document.activeElement.id === 'global-search-input') {
        // If focus is currently on the active search input, return focus to the placeholder input.
        searchPlaceholderInput.focus();
      }
    }
  };

  componentDidMount() {
    document.addEventListener('keydown', this.escapeKeyHandler);
    document.addEventListener('openAIChat', this.onToggleSidebar);
    document.addEventListener('openHelpCenter', this.onToggleHelpCenter);
    document.addEventListener('skipMenuList', this.setSkipMenuList);
    document.addEventListener('toastSupressed', this.setToastSupressed);
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.escapeKeyHandler);
    document.removeEventListener('openAIChat', this.onToggleSidebar);
    document.removeEventListener('openHelpCenter', this.onToggleHelpCenter);
    document.removeEventListener('skipMenuList', this.setSkipMenuList);
    document.removeEventListener('toastSupressed', this.setToastSupressed);
  }

  getTitle(size, isWorkspacesAvailable) {
    // Both titles should always be set as defaults and fallbacks are determined in Core
    const {solutionTitle, solutionShortTitle} = this.props;
    switch (size) {
      case 'XS':
      case 'S':
        return shortTitleToRender(solutionShortTitle, isWorkspacesAvailable, size);
      case 'M':
        if (!isWorkspacesAvailable) {
          return solutionTitle;
        }
        return solutionShortTitle;
      default:
        return solutionTitle;
    }
  }

  /**
   * Gets proper label based on dev environment.
   * @returns {string} Proper string for dev environment label.
   */
  getEnvironmentLabel(environment) {
    if (this.props.devMode) {
      return 'Dev Mode';
    }

    switch (environment) {
      case 'dev':
        return 'Dev';
      case 'qa':
        return 'QA';
      case 'stage':
        return 'Staging';
      case 'test':
        return 'Test';
    }
  }

  /**
   * We need to support two variants of global Search
   * 1. Searchbar in the center of the header
   * 2. Magnify Icon in Actions bar.
   * Based on the screen size and availability of workspaces,
   * we decide which variant of global search needs to be displayed in the header.
   * @param searchProps - Various props to pass to unified search
   * @param searchProps.appId
   * @param searchProps.globalSearch
   * @param searchProps.globalSearchProps
   * @param searchProps.isWorkspacesAvailable
   * @param searchProps.leftContainer
   * @param searchProps.size
   * @param searchProps.locale
   * @param searchProps.registerKeyHandler
   * @param searchProps.services
   * @param searchProps.theme
   * @param searchProps.sandboxes
   * @param position
   * @returns {React.JSX.Element|null}
   */
  checkAndReturnUnifiedSearch = (searchProps, position) => {
    const {
      appId,
      backgroundReady,
      featureFlags,
      globalSearch,
      globalSearchProps,
      isWorkspacesAvailable,
      leftContainer,
      maxFullSearchSize,
      size,
      locale,
      registerKeyHandler,
      sandboxes,
      services,
      solutions,
      tabIndex,
      theme,
      userEmail
    } = searchProps;
    /**
     * If isWorkspacesAvailable we are not sure if there'd be enough space available
     * for the searchbar or not, so the ask is to show magnify icon instead.
     * @type {string|*}
     */
    const mode = (isWorkspacesAvailable > 0 || globalSearchProps.compact || maxFullSearchSize) ? 'S' : size;
    if (!globalSearchProps.enabled || !backgroundReady) {
      return null;
    }
    let isUnifiedSearch = true;
    if ((mode !== 'L' && position !== 'actions') || (mode === 'L' && position !== 'searchbar')) {
      isUnifiedSearch = false;
    }
    return (
      <React.Suspense fallback={null}>
        <globalSearchProps.SearchWidget
          accessToken={globalSearchProps.accessToken}
          appId={appId}
          closeOverlaysOnMenuShow={this.closeOverlaysOnMenuShow}
          featureFlags={featureFlags}
          getFullUrl={getFullUrl}
          hashToPath={hashToPath}
          internal={globalSearchProps.internal}
          isUnifiedSearch={isUnifiedSearch}
          leftContainer={leftContainer(true)}
          locale={locale}
          mode={mode}
          onToggleGlobalSearch={this.onToggleGlobalSearch}
          open={globalSearch.open}
          recents={globalSearchProps.recents}
          registerKeyHandler={registerKeyHandler}
          sandboxes={sandboxes}
          scope={globalSearchProps.parent}
          selectedImsOrg={globalSearchProps.selectedImsOrg}
          selectedOrgName={globalSearchProps.selectedOrgName}
          services={services}
          settingsService={this.props.settingsService}
          shellOverlayTrigger={ShellOverlayTrigger}
          size={size === 'XS' ? 'S' : size}
          solutions={solutions}
          tabIndex={tabIndex}
          theme={theme}
          updatePath={updatePathSearch}
          userEmail={userEmail}>
        </globalSearchProps.SearchWidget>
      </React.Suspense>
    );
  };

  renderImpersonationLabel = () => {
    const {isImpersonating, userFirstName, userLastName} = this.props;
    if (!isImpersonating) {
      return;
    }
    return (
      <Badge variant="magenta">
        <Visibility size="S" />
        <Text UNSAFE_className="colorBadge impersonationBadge">{userFirstName} {userLastName}</Text>
      </Badge>
    );
  };

  renderEnvLabel = () => {
    const {accessOverridesEnabled, customEnvLabel, environment, feedback, intl} = this.props;
    const envLabel = this.getEnvironmentLabel(environment);
    return (
      <div className={classNames('colorBadge-container', {hide: !feedback || !feedback.enabled})}>
        <Flex gap="size-125">
          {envLabel && (
            <Badge
              variant="yellow"
            >
              <Text UNSAFE_className="colorBadge">{envLabel}</Text>
            </Badge>
          )}
          {(customEnvLabel || []).slice(0, 2).map((label, index) => (label && (
            <Badge
              key={index}
              variant="seafoam"
            >
              <Text UNSAFE_className="colorBadge">{label}</Text>
            </Badge>
          )))}
          {accessOverridesEnabled && (
            <Badge variant="negative">
              <Text UNSAFE_className="colorBadge">
                {intl.formatMessage({
                  defaultMessage: 'Overrides on',
                  description: 'Overrides on badge label',
                  id: 'overridesEnabledBadgeLabel'
                })}
              </Text>
            </Badge>
          )}
        </Flex>
      </div>
    );
  };

  renderReleaseBadge = () => {
    const {intl, solutionReleaseType} = this.props;
    if (solutionReleaseType === RELEASE_TYPE.GA) {
      // If the hero/top-level solution is GA,
      // we don't need to show the release badge
      return;
    }
    const RELEASE_MESSAGES = {
      [RELEASE_TYPE.ALPHA]: intl.formatMessage({
        defaultMessage: 'Alpha',
        description: 'Label for ALPHA service lifecycle',
        id: 'releaseBadgeAlpha'
      }),
      [RELEASE_TYPE.BETA]: intl.formatMessage({
        defaultMessage: 'Beta',
        description: 'Label for BETA service lifecycle',
        id: 'releaseBadgeBeta'
      }),
      [RELEASE_TYPE.DEV]: intl.formatMessage({
        defaultMessage: 'Dev',
        description: 'Label for DEV service lifecycle',
        id: 'releaseBadgeDev'
      }),
      [RELEASE_TYPE.POC]: intl.formatMessage({
        defaultMessage: 'POC',
        description: 'Label for POC service lifecycle',
        id: 'releaseBadgePoc'
      })
    };

    return (
      <div className="colorBadge-container">
        <Flex gap="size-125">
          <Badge
            UNSAFE_className={`releaseBadge-${solutionReleaseType}`}
            variant={RELEASE_TYPE_VARIANTS[solutionReleaseType]}
          >
            <Text UNSAFE_className="colorBadge">{RELEASE_MESSAGES[solutionReleaseType]}</Text>
          </Badge>
        </Flex>
      </div>
    );
  };

  renderWorkspaces = () => {
    const {selectedWorkspaceId, showMoreActionsButton, size, workspaces} = this.props;
    return (
      <>
        {workspaces.length > 0 && (
          <React.Suspense fallback={null}>
            <ShellWorkspace
              leftContainerElementOrderSwitched={this.getTitle(size, workspaces.length > 0) === ''}
              selected={selectedWorkspaceId}
              showMoreActionsButton={showMoreActionsButton}
              workspaces={workspaces} />
          </React.Suspense>
        )}
      </>
    );
  };

  /**
   * When resolution is low and logo brand text is not present,
   * switch workspaces and env label positioning.
   */
  leftContainerElementOrderSwitcher = () => {
    const {feedback, showMoreActionsButton, size, workspaces} = this.props;
    const title = this.getTitle(size, workspaces.length > 0);
    // Show environment labels in header when they are not collapsed in More Actions button
    // or when feedback button isn't present (when feedback button is present,
    // they go into the second bar of the More Actions).
    const showEnvLabel = (!showMoreActionsButton || (!(feedback || {}).enabled));
    const showImpersonationLabel = (!showMoreActionsButton || (!(feedback || {}).enabled));
    return (
      <Flex alignItems="center" direction="row" gap="size-125" marginEnd="size-250">
        {!title && this.renderWorkspaces()}
        {this.renderReleaseBadge()}
        {showEnvLabel ? this.renderEnvLabel() : null}
        {showImpersonationLabel ? this.renderImpersonationLabel() : null}
        {title && this.renderWorkspaces()}
      </Flex>
    );
  };

  /**
   * The leftContainer is passed and used by Global Search component,
   * and in case search component we don't need to show workspaces.
   * So the parameter "inSearchPopover" conditionally controls whether the leftContainer
   * should return with workspaces or not.
   * The Global Search component show the left container in an overlay
   * which should be the exact copy(Including icon/text and env label)
   * of what we show in shell header,
   * which is why we pass the left container in search container.
   * @param inSearchPopover
   * @returns {React.JSX.Element}
   */
  leftContainer = inSearchPopover => {
    const {
      basePath,
      customHeroClick,
      intl,
      onSolutionClick,
      sideNavEnabled,
      sideNavOpen,
      sideNavOpenByHover,
      size,
      solutionIcon,
      solutionTitle,
      theme,
      toggleSideNav,
      workspaces
    } = this.props;
    const {skipMenuList, skipMenuOpen} = this.state;
    const title = this.getTitle(size, workspaces.length > 0);
    const {locale} = intl;
    return (
      <div className="spectrum-Shell-leftContainer">
        <React.Suspense fallback={null}>
          <I18nShellSkipMenu
            items={skipMenuList}
            locale={locale}
            onVisibilityChange={this.onSkipMenuVisibilityChange}
            open={skipMenuOpen}
            role="button"
            tabIndex="0"
            trackSkipMenuActions={this.trackSkipMenuActions}
          />
        </React.Suspense>
        {sideNavEnabled && <SideNavMenuButton
          sideNavOpen={sideNavOpen}
          sideNavOpenByHover={sideNavOpenByHover}
          toggleSideNav={toggleSideNav}
        />}
        <ActionButton
          UNSAFE_className="spectrum-Shell-logoIcon"
          data-omega-attribute-icon={solutionTitle}
          data-omega-attribute-link={customHeroClick ? 'custom' : basePath}
          data-omega-element={title}
          data-omega-widget="hero"
          data-omega-widget-type="button"
          isQuiet
          marginEnd={title ? 'size-125' : 0}
          onPress={onSolutionClick}
        >
          <HeroImage hasTitle={!!title} icon={solutionIcon} theme={theme} title={solutionTitle} />
          {title && <Text>{title}</Text>}
        </ActionButton>
        {!inSearchPopover && this.leftContainerElementOrderSwitcher()}
      </div>
    );
  };

  render() {
    const {
      aiSidebarEnabled,
      aiToggleRef,
      appContainer,
      appId,
      appRoot,
      backgroundReady,
      coachMark,
      currentActiveTenantId,
      customProfileButtons,
      customSearch,
      environment,
      featureFlags,
      feedback,
      globalSearch: globalSearchProps,
      hasAccountClusterData,
      hasSandboxBar,
      helpCenterConfig,
      imsOrgs,
      internal,
      intl,
      landingpage,
      maxFullSearchSize,
      modal,
      noAccess,
      onCustomProfileButtonClick,
      onImsOrgChange,
      onSetUserTheme,
      onSignOutClick,
      orgToSubOrgsMap,
      pulse,
      recentSandboxes,
      registerKeyHandler,
      sandbox,
      selectedImsOrg,
      selectedSubOrg,
      services,
      settingsData,
      settingsService,
      showDebugModal,
      showMoreActionsButton,
      sideNavEnabled,
      sidebarOpen,
      size,
      solutions,
      solutionTitle,
      subOrgAccessOnlyOnCurrentOrg,
      theme,
      translationService,
      userAvatar,
      userEmail,
      userFirstName,
      userLastName,
      userName,
      userRecentSandboxesHandler,
      userTitle,
      workspaces
    } = this.props;
    const {formatMessage, locale} = intl;
    const {globalSearch, helpCenterProps, skipMenuOpen, toastsSupressedCount} = this.state;

    const unifiedSearchProps = {
      appId,
      backgroundReady,
      featureFlags,
      globalSearch,
      globalSearchProps,
      isWorkspacesAvailable: workspaces.length > 0,
      leftContainer: this.leftContainer,
      locale,
      maxFullSearchSize,
      registerKeyHandler,
      sandboxes: sandbox ? sandbox.sandboxes : [],
      services,
      size,
      solutions,
      theme,
      userEmail
    };

    const showCoachMark = coachMark && coachMark.enabled && !coachMark.hidden;
    const showUnderlay = showCoachMark || !appContainer && modal || skipMenuOpen;
    const providerProps = {
      locale,
      theme: theme === 'darkest' ? 'darkest' : 'light',
      toastPlacement: this.props.toastPlacement
    };
    const searchBarSize = (workspaces.length > 0 || globalSearchProps.compact || maxFullSearchSize) ? 'S' : size;
    return (
      <Provider theme={defaultTheme}>
        <SpectrumV2Provider {...providerProps}>
          <header
            aria-hidden={showCoachMark || modal}
            className={
              classNames('spectrum-Shell', `spectrum--${theme}`, {
                'has-appContainer': appContainer,
                'modal-open': modal,
                // extra class needed for the spectrum's light stylesheet
                'spectrum--light': theme === 'lightest'
              })
            }
          >
            <div className={classNames('spectrum-Underlay', {'is-open': showUnderlay})} style={{position: 'absolute'}}></div>
            <div className={`spectrum-Shell-headingContainer ${workspaces.length > 0 ? 'workspacesPresent' : 'noWorkspaces'}`}>
              {showCoachMark &&
                <React.Suspense fallback={null}>
                  <CoachMark
                    selector={coachMark.element}
                    title=""
                  />
                </React.Suspense>
              }
              {this.leftContainer()}
              {searchBarSize === 'L' && this.checkAndReturnUnifiedSearch(unifiedSearchProps, 'searchbar')}
              <div className="spectrum-Shell-rightContainer">
                <IntlProvider messages={localizationStringsShellAction}>
                  <ShellHeaderActions
                    aiSidebarEnabled={aiSidebarEnabled}
                    aiToggleRef={aiToggleRef}
                    appId={appId}
                    appRoot={appRoot}
                    checkAndReturnUnifiedSearch={this.checkAndReturnUnifiedSearch}
                    closeOverlaysOnMenuShow={this.closeOverlaysOnMenuShow}
                    currentActiveTenantId={currentActiveTenantId}
                    customProfileButtons={customProfileButtons}
                    customSearch={customSearch}
                    environment={environment}
                    feedback={feedback}
                    formatMessage={formatMessage}
                    hasAccountClusterData={hasAccountClusterData}
                    hasSandboxBar={hasSandboxBar}
                    helpCenterConfig={helpCenterConfig}
                    helpCenterProps={helpCenterProps}
                    imsOrgs={imsOrgs}
                    internal={internal}
                    landingpage={landingpage}
                    locale={locale}
                    metrics={this.metrics}
                    noAccess={noAccess}
                    onCustomProfileButtonClick={onCustomProfileButtonClick}
                    onImsOrgChange={onImsOrgChange}
                    onSetUserTheme={onSetUserTheme}
                    onSignOutClick={onSignOutClick}
                    onToggleHelpCenter={this.onToggleHelpCenter}
                    onToggleSidebar={this.props.onToggleSidebar}
                    orgToSubOrgsMap={orgToSubOrgsMap}
                    pulse={pulse}
                    recentSandboxes={recentSandboxes}
                    renderEnvLabel={this.renderEnvLabel}
                    renderImpersonationLabel={this.renderImpersonationLabel}
                    renderReleaseBadge={this.renderReleaseBadge}
                    sandbox={sandbox}
                    selectedImsOrg={selectedImsOrg}
                    selectedSubOrg={selectedSubOrg}
                    services={services}
                    settingsData={settingsData}
                    settingsService={settingsService}
                    showCollapsedSearch={searchBarSize !== 'L'}
                    showDebugModal={showDebugModal}
                    showMoreActionsButton={showMoreActionsButton}
                    sideNavEnabled={sideNavEnabled}
                    sidebarOpen={sidebarOpen}
                    size={size}
                    solutionTitle={solutionTitle}
                    solutions={solutions}
                    subOrgAccessOnlyOnCurrentOrg={subOrgAccessOnlyOnCurrentOrg}
                    theme={theme}
                    toastsSupressedCount={toastsSupressedCount}
                    translationService={translationService}
                    unifiedSearchProps={unifiedSearchProps}
                    userAvatar={userAvatar}
                    userEmail={userEmail}
                    userFirstName={userFirstName}
                    userLastName={userLastName}
                    userName={userName}
                    userRecentSandboxesHandler={userRecentSandboxesHandler}
                    userTitle={userTitle}
                  />
                </IntlProvider>
              </div>
            </div>
            {this.props.children}
          </header>
        </SpectrumV2Provider>
      </Provider>
    );
  }
}

ShellHeader.defaultProps = {
  aiSidebarEnabled: false,
  backgroundReady: false,
  customSearch: {
    enabled: false,
    onClick: NOOP,
    open: false
  },
  disableShellFeedbackButton: PropTypes.bool,
  feedback: null,
  globalSearch: {},
  helpCenterConfig: {},
  imsOrgs: [],
  locale: 'en-US',
  modal: false,
  noAccess: false,
  onGlobalDialogOpen: NOOP,
  onImsOrgChange: NOOP,
  onSignOutClick: NOOP,
  onSolutionClick: NOOP,
  onWorkspaceChange: NOOP,
  pulse: {
    onPulseViewAllClick: NOOP
  },
  registerKeyHandler: NOOP,
  selectedWorkspaceId: '',
  settingsData: {},
  sideNavEnabled: false,
  size: 'L',
  solutionReleaseType: RELEASE_TYPE.GA,
  solutionShortTitle: 'Adobe Experience Cloud',
  solutionTitle: 'Adobe Experience Cloud',
  toastPlacement: 'top center',
  toggleSideNav: NOOP,
  userAvatar: undefined,
  userName: 'Adobe User',
  userTitle: undefined,
  workspaces: []
};

ShellHeader.propTypes = {
  aiSidebarEnabled: PropTypes.bool,
  aiToggleRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({current: PropTypes.any})
  ]),
  backgroundReady: PropTypes.bool,
  basePath: PropTypes.string,
  customHeroClick: PropTypes.func,
  customSearch: PropTypes.shape({
    enabled: PropTypes.bool,
    onClick: PropTypes.func,
    open: PropTypes.bool
  }),
  devMode: PropTypes.bool,
  feedback: PropTypes.shape({
    enabled: PropTypes.bool,
    type: PropTypes.string
  }),
  hasAccountClusterData: PropTypes.bool,
  helpCenterConfig: PropTypes.shape({
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        href: PropTypes.string,
        icon: PropTypes.string,
        label: PropTypes.string
      })
    )
  }),
  imsOrgs: PropTypes.arrayOf(
    PropTypes.shape({
      imsOrgId: PropTypes.string,
      imsOrgName: PropTypes.string,
      tenantId: PropTypes.string
    })
  ).isRequired,
  locale: PropTypes.string,
  /** Whether a modal is showing or not */
  modal: PropTypes.bool,
  noAccess: PropTypes.bool,
  onGlobalDialogOpen: PropTypes.func,
  onImsOrgChange: PropTypes.func,
  onSignOutClick: PropTypes.func,
  /** Is triggered when the solution name is clicked */
  onSolutionClick: PropTypes.func,
  /** Is triggered when the workspace is changed */
  onWorkspaceChange: PropTypes.func,
  pulse: PropTypes.shape({
    accessToken: PropTypes.boolean,
    additionalCount: PropTypes.number,
    customButton: PropTypes.object,
    enabled: PropTypes.boolean,
    onPulseViewAllClick: PropTypes.func,
    pulseContainer: PropTypes.element
  }),
  recentSandboxes: PropTypes.array,
  registerKeyHandler: PropTypes.func,
  selectedImsOrg: PropTypes.string,
  selectedSubOrg: PropTypes.object,
  /** Is triggered when the solution name is clicked */
  selectedWorkspaceId: PropTypes.string,
  settingsData: PropTypes.object,
  settingsService: PropTypes.object,
  showDebugModal: PropTypes.func,
  sideNavEnabled: PropTypes.bool,
  /** Size variant: S/M/L */
  size: PropTypes.oneOf(['XS', 'S', 'M', 'L']),
  /** The solution icon or image */
  solutionIcon: PropTypes.string,
  solutionReleaseType: PropTypes.oneOf(Object.values(RELEASE_TYPE)),
  /** The short name of the solution */
  solutionShortTitle: PropTypes.string,
  /** The full name of the solution */
  solutionTitle: PropTypes.string,
  toastPlacement: PropTypes.string,
  toggleSideNav: PropTypes.func,
  userAvatar: PropTypes.string,
  userFirstName: PropTypes.string,
  userLastName: PropTypes.string,
  userName: PropTypes.string,
  userRecentSandboxesHandler: PropTypes.func,
  userTitle: PropTypes.string,
  /** List of workspaces to show in the left side of the shell */
  workspaces: PropTypes.arrayOf(
    PropTypes.shape({
      /** Size variant: S/M/L */
      name: PropTypes.string,
      /** URL for workspace item */
      url: PropTypes.string
    })
  )
};

const I18nHeader = injectIntl(ShellHeader);

const LocalizedHeader = props => (
  <IntlProvider messages={localizationStringsShell}>
    <I18nHeader {...props} />
  </IntlProvider>
);

LocalizedHeader.propTypes = {
  locale: PropTypes.string
};

LocalizedHeader.defaultProps = {
  locale: 'en-US'
};

export {I18nHeader as ShellHeader};
export default React.memo(LocalizedHeader);
